import { createTheme } from '@mui/material/styles';
import { palette, derived, semantic } from './tokens';

/**
 * Tema MUI — consome os mesmos primitivos que `global.ts`.
 *
 * MUI e styled-components precisam concordar: um componente MUI e um styled-component
 * com o mesmo papel devem exibir exatamente a mesma cor (SC-001).
 *
 * Aqui os valores vêm de `tokens.ts` em vez de `var(--token)` porque o MUI precisa
 * calcular variações (hover, ripple, contrastText) em tempo de criação do tema, e
 * `var()` só resolve no navegador.
 *
 * Ver contracts/tokens.contract.md (C2).
 */
export const theme = createTheme({
	palette: {
		primary: {
			// Botões primários: Deep Graphite com texto Off White (13.97:1)
			main: palette.graphite,
			light: palette.slate,
			dark: palette.graphiteDark,
			contrastText: palette.offWhite,
		},
		secondary: {
			// Destaque: Digital Teal como SUPERFÍCIE, com texto Deep Graphite (7.00:1).
			// Teal reprova como texto sobre fundo claro (1.99:1) — nunca usar assim.
			main: palette.teal,
			light: palette.mint,
			dark: derived.tealDeep,
			contrastText: palette.graphite,
		},
		error: {
			main: semantic.error,
			contrastText: palette.white,
		},
		warning: {
			main: semantic.warning,
			contrastText: palette.white,
		},
		info: {
			main: semantic.info,
			contrastText: palette.white,
		},
		success: {
			main: semantic.success,
			contrastText: palette.white,
		},
		text: {
			primary: palette.graphite,
			secondary: palette.slate,
			disabled: derived.slateLight,
		},
		background: {
			default: palette.offWhite,
			paper: palette.white,
		},
		divider: palette.stone,
	},
	shape: {
		borderRadius: 8,
	},
	typography: {
		fontFamily: "'Inter', system-ui, -apple-system, sans-serif",
		h1: {
			fontFamily: "'Source Serif 4', Georgia, serif",
			fontSize: '2.5rem',
			letterSpacing: '-0.02em',
			lineHeight: 1.2,
		},
		h2: {
			fontFamily: "'Source Serif 4', Georgia, serif",
			fontSize: '1.75rem',
			letterSpacing: '-0.02em',
			lineHeight: 1.2,
		},
		h3: {
			fontFamily: "'Source Serif 4', Georgia, serif",
			letterSpacing: '-0.01em',
		},
		h4: { fontFamily: "'Source Serif 4', Georgia, serif" },
		h5: { fontFamily: "'Source Serif 4', Georgia, serif" },
		h6: { fontFamily: "'Source Serif 4', Georgia, serif" },
		body1: { lineHeight: 1.55 },
		body2: { lineHeight: 1.55 },
		caption: {
			textTransform: 'uppercase',
			letterSpacing: '0.08em',
			fontSize: '0.75rem',
		},
	},
	components: {
		MuiButton: {
			styleOverrides: {
				root: {
					borderRadius: '8px',
					textTransform: 'none',
				},
			},
			variants: [
				{
					props: { color: 'secondary' },
					style: {
						border: `1px solid ${derived.tealMid}`,
						color: palette.graphite,
						' > .MuiTouchRipple-root span': {
							color: derived.tealDeep,
						},
					},
				},
			],
		},
	},
});
